import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { Company, CompanySortOptions } from '@/api/v4/organization.api';
import { selectedCompaniesState } from '@/features/companies/companies.state';
import { companiesHeaders } from '@/features/companies/companies-table-configuration';
import { CompanyActionsMenu } from '@/features/companies/company-actions-menu';
import { formatDate } from '@/helpers/format-date';
import { paths } from '@/routes/helpers/paths';
import { modalState } from '@/state/modal.state';
import { colors } from '@/theme/colors';
import { Button } from '@/ui/button/button';
import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';
import type { Row } from '@/ui/table/table.types';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { RelatedProjectsCount } from '../related-projects/related-projects-count';
import { CellLink } from '@/features/search/global-search/search-suggestions/enriched-table/ui';
import { CompanyRowMainCell } from '@/features/search/global-search/search-suggestions/company-suggestions-table/company-row-main-cell';
import { TextCell } from '@/features/search/global-search/search-suggestions/enriched-table/text-cell';
import { getAbbreviateNumber } from '@/helpers/format-number';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Checkbox } from '@/ui/checkbox/checkbox';
import { TagsPreview } from '@/features/tags/tags-preview';
import { useAtom, useSetAtom } from 'jotai';

const rowPadding = {
  left: '20px',
  right: '0px',
};

interface UseCompaniesTableConfigurationProps {
  companies: Company[];
  isLoading?: boolean;
  onSelect?: (id: string) => void;
}

export const useCompaniesTableConfiguration = ({
  companies,
}: UseCompaniesTableConfigurationProps) => {
  const [selectedCompanies, setSelectedCompanies] = useAtom(
    selectedCompaniesState,
  );

  const header = useTranslateHeaders<CompanySortOptions>(
    companiesHeaders.map(item => {
      if (item.showCheckbox) {
        item.onCheckboxChange = isChecked => {
          setSelectedCompanies(
            isChecked
              ? companies.map(company => ({ organizationId: company.id }))
              : [],
          );
        };

        item.checkboxCount = companies.length;
        if (selectedCompanies.length) {
          item.isChecked = Boolean(selectedCompanies.length);
          item.checkboxIcon =
            selectedCompanies.length !== companies.length
              ? 'CheckMarkMinus'
              : undefined;
          item.checkboxActions = <CompanyActions />;
        } else {
          item.isChecked = false;
          item.checkboxActions = undefined;
        }
      }

      return item;
    }),
    'companies',
    'companies',
  );

  const rows: Row[] = useMemo(
    () =>
      companies.map(company => {
        return {
          id: company.id,
          rowPadding,
          ...(selectedCompanies.some(
            selected => selected.organizationId === company.id,
          ) && {
            rowBackgroundColor: colors.blue.c7,
          }),
          cells: [
            {
              padding: '0 10px',
              value: <CompanyNameTableCell company={company} />,
            },
            {
              align: 'center',
              padding: '0 10px',
              minWidth: '320px',

              value: (
                <CellLink to={paths.company({ companyId: company.id })}>
                  <TagsPreview tags={company.tags} />
                </CellLink>
              ),
            },
            {
              align: 'center',
              padding: '0 10px',
              value: (
                <CellLink to={paths.company({ companyId: company.id })}>
                  <RelatedProjectsCount
                    value={company.projectListingsCount}
                    companyId={company.id}
                  />
                </CellLink>
              ),
            },
            {
              padding: '0 10px',

              value: (
                <CellLink to={paths.company({ companyId: company.id })}>
                  <TextCell value={company.yearEstablished} />
                </CellLink>
              ),
            },
            {
              padding: '0 10px',

              value: (
                <CellLink to={paths.company({ companyId: company.id })}>
                  <TextCell value={company.fundingStage} />
                </CellLink>
              ),
            },
            {
              padding: '0 10px',

              value: (
                <CellLink to={paths.company({ companyId: company.id })}>
                  <TextCell
                    value={
                      company.capitalRaised
                        ? `$${getAbbreviateNumber(company.capitalRaised, 1)}`
                        : ''
                    }
                  />
                </CellLink>
              ),
            },
            {
              align: 'center',
              value: (
                <CellLink to={paths.company({ companyId: company.id })}>
                  <TextCell
                    value={
                      company.lastEditedDate
                        ? formatDate(company.lastEditedDate, {
                            format: 'dd MMM yyyy',
                          })
                        : ''
                    }
                  />
                </CellLink>
              ),
            },

            {
              value: <CompanyActionsMenu company={company} />,
              hoverOnly: true,
              verticalAlign: 'middle',
              padding: '0',
            },
          ],
        };
      }),
    [companies, selectedCompanies],
  );

  return {
    header,
    rows,
    skeletonConfig: {
      rowPadding,
      // minus one because of actions column that is invisible on load
      columns: header.cells.length - 1,
      numberOfRows: 6,
    },
  };
};

const CompanyNameTableCell = ({ company }: { company: Company }) => {
  const [selectedCompanies, setSelectedCompanies] = useAtom(
    selectedCompaniesState,
  );

  const handleSelectCompany = useCallback(() => {
    setSelectedCompanies(old => {
      if (old.some(someCompany => someCompany.organizationId === company.id)) {
        return old.filter(
          someCompany => someCompany.organizationId !== company.id,
        );
      }
      return [...old, { organizationId: company.id }];
    });
  }, [company.id, setSelectedCompanies]);

  return (
    <Flexbox alignItems="center" gap="20px">
      <Checkbox
        id={company.id}
        label=""
        checked={selectedCompanies.some(
          item => item.organizationId === company.id,
        )}
        onChange={handleSelectCompany}
      />
      <CellLink to={paths.company({ companyId: company.id })}>
        <CompanyRowMainCell
          name={company.name}
          location={company.location}
          logoUrl={company.logoUrl}
          keyOffering={company.keyOffering}
          website={company.website}
          linkedInUrl={company.linkedInUrl}
          isEnrichDataLoading={false}
        />
      </CellLink>
    </Flexbox>
  );
};

export const CompanyActions = () => {
  const { t } = useTranslation('companies');
  const setModalState = useSetAtom(modalState);
  const { hasRequiredPermission } = useUserPermissions();

  return (
    <>
      {hasRequiredPermission(PERMISSION.ADD_COMPANIES_TO_PROJECTS) && (
        <AddToProjectButton
          variant="text"
          startIcon="Plus"
          onClick={e => {
            e.stopPropagation();
            setModalState({ state: 'addCompanyToProjects' });
          }}
        >
          {t`companies.checkboxActions.addToProject`}
        </AddToProjectButton>
      )}

      {hasRequiredPermission(PERMISSION.DELETE_ORGANIZATION) && (
        <Button
          variant="dangerText"
          startIcon="Bin"
          onClick={e => {
            e.stopPropagation();
            setModalState({
              state: 'deleteCompanies',
            });
          }}
        >
          {t`companies.checkboxActions.delete`}
        </Button>
      )}
    </>
  );
};

const AddToProjectButton = styled(Button)`
  color: ${({ theme }) => theme.colors.blue.primaryA};
  &:hover {
    color: ${({ theme }) => theme.colors.blue.primaryA};
  }
`;
